import { useCallback, useEffect, useRef, useState } from 'react'

type DisplayArea = {
  width: number
  height: number
  offsetX: number
  offsetY: number
}

/**
 * object-fit: contain で表示された画像上に、検査箇所（areas）のオーバーレイを正確に重ねるためのhook。
 *
 * areasの座標は画像の自然サイズに対する比率（0〜1）で保存されているが、
 * object-fit: contain ではコンテナと画像のアスペクト比の差により余白が生じるため、
 * 画像が実際に描画される領域のサイズとオフセットを計算する必要がある。
 * （もっとイージーな方法があればいつでも変えて欲しい...）
 */
export const useDisplayArea = () => {
  const imgRef = useRef<HTMLImageElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [displayArea, setDisplayArea] = useState<DisplayArea | null>(null)

  const calculateDisplayArea = useCallback(() => {
    const img = imgRef.current
    const container = containerRef.current

    if (!img || !container || !img.complete) return

    const containerWidth = container.clientWidth
    const containerHeight = container.clientHeight
    const imgAspectRatio = img.naturalWidth / img.naturalHeight
    const containerAspectRatio = containerWidth / containerHeight

    if (imgAspectRatio > containerAspectRatio) {
      setDisplayArea({
        width: containerWidth,
        height: containerWidth / imgAspectRatio,
        offsetX: 0,
        offsetY: (containerHeight - containerWidth / imgAspectRatio) / 2,
      })
    } else {
      setDisplayArea({
        width: containerHeight * imgAspectRatio,
        height: containerHeight,
        offsetX: (containerWidth - containerHeight * imgAspectRatio) / 2,
        offsetY: 0,
      })
    }
  }, [])

  useEffect(() => {
    const img = imgRef.current
    if (img?.complete) {
      calculateDisplayArea()
    }

    const handleResize = () => calculateDisplayArea()
    window.addEventListener('resize', handleResize)

    return () => window.removeEventListener('resize', handleResize)
  }, [calculateDisplayArea])

  return {
    imgRef,
    containerRef,
    displayArea,
    onImageLoad: calculateDisplayArea,
  }
}
